import { tokenManager } from '../utils/api';
import { USER_URL } from '../config/environment';
import axios from 'axios';

/**
 * Custom Domain Service
 * Handles all custom domain-related API calls
 */

export const domainService = {
  /**
   * Get domains by account
   * @param {string} accountId - Account ID
   * @returns {Promise<Array>} Domains list
   */
  getDomainsByAccount: async (accountId) => {
    const response = await axios.get(`${USER_URL}/api/account-configs/${accountId}/domains`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    
    
    // Handle the nested response structure
    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    }
    
    // Fallback for different response structures
    return response.data.data || response.data || [];
  },

  /**
   * Add custom domain
   * @param {Object} domainData - Domain data
   * @returns {Promise<Object>} Added domain data
   */
  addCustomDomain: async (domainData) => {
    const response = await axios.post(`${USER_URL}/api/domains/add`, domainData, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Add domain to account
   * @param {string} accountId - Account ID
   * @param {string} domain - Domain to add
   * @returns {Promise<Object>} Add domain response
   */
  addDomainToAccount: async (accountId, domain) => {
    const response = await axios.post(`${USER_URL}/api/account-configs/${accountId}/domains`, {
      domain
    }, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    
    
    // Handle the nested response structure
    if (response.data && response.data.success && response.data.data) {
      return response.data.data;
    }
    
    // Fallback for different response structures
    return response.data.data || response.data;
  },

  /**
   * Delete custom domain
   * @param {string} domainId - Domain ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteCustomDomain: async (domainId) => {
    const response = await axios.delete(`${USER_URL}/api/account-configs/domains/${domainId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Set default domain for account (Admin/SuperAdmin only)
   * @param {string} accountId - Account ID
   * @param {string} domainId - Domain ID to set as default
   * @returns {Promise<Object>} Response data
   */
  setDefaultDomain: async (accountId, domainId) => {
    const response = await axios.put(`${USER_URL}/api/account-configs/${accountId}/default-domain`, null, {
      params: { domainId },
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  verifyDomain: async (domainId) => {
    const response = await axios.post(`${USER_URL}/api/account-configs/domains/${domainId}/verify`, {}, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  checkDomain: async (accountId, domain) => {
    const response = await axios.post(`${USER_URL}/api/account-configs/check-domain`, { accountId, domain }, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  },

  /**
   * Delete domain (alias for deleteCustomDomain)
   * @param {string} domainId - Domain ID
   * @returns {Promise<Object>} Deletion response
   */
  deleteDomain: async (domainId) => {
    const response = await axios.delete(`${USER_URL}/api/account-configs/domains/${domainId}`, {
      headers: { Authorization: `Bearer ${tokenManager.getAccessToken()}` }
    });
    return response.data;
  }
};

export default domainService;
